<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Cele KPI</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja celu</template>
          <template v-else>Dodawanie celu</template>
        </h3>
      </d-col>
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/cele')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <div
          v-if="!allDataLoaded"
          class="loading-spinner pb-3 pt-3 text-center">
          <span class="loading-spinner-content text-primary">
            Trwa ładowanie danych&hellip;
          </span>
        </div>
        <d-tabs
          card
          :class="{'is-loading': !allDataLoaded}">
          <!-- Dane ogólne -->
          <d-tab
            title="Dane ogólne"
            active
            class="width-narrow px-3 px-md-4">
            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <validation-observer
              ref="goal-form"
              tag="form">
              <d-row>
                <d-col sm="12">
                  <div class="form-group">
                    <label for="user">Pracownik:</label>

                    <validation-provider
                      name="user"
                      rules="required"
                      v-slot="{ errors }">
                      <div
                        :class="{
                          'form-control': true,
                          'is-select': true,
                          'is-invalid': errors.length,
                          'vs-wrap-text': true
                        }">
                        <v-select
                          :disabled="!allDataLoaded"
                          :options="allUsers"
                          v-model="data.user_id"
                          :reduce="(user) => user.id"
                          placeholder="Wybierz pracownika"
                          :searchable="true"
                          label="name">
                        </v-select>
                      </div>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
              <d-row class="mb-3">
                <d-col sm="6">
                  <div class="form-group">
                    <label for="amount">
                      Kwota do osiągnięcia
                    </label>
                    <validation-provider
                      rules="required|min_value:1"
                      name="amount"
                      v-slot="{ errors }">
                      <d-input-group
                        append="zł">
                        <d-input
                          :min="1"
                          class="text-right"
                          v-model="data.amount"
                          :state="errors.length ? 'invalid' : null"
                          type="number" />
                      </d-input-group>
                      <div v-if="errors.length" class="invalid-feedback d-block">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col sm="6">
                  <div
                    class="form-group"
                    v-if="dateRangePickerVisible">
                    <label for="valid-from">
                      Ważny od:
                    </label>

                    <validation-provider
                      rules="required"
                      name="valid-from"
                      v-slot="{ errors }">
                      <date-range-picker
                        ref="picker"
                        :locale-data="{
                          'firstDay': 1,
                          'format': 'DD-MM-YYYY',
                          'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
                          'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
                          'applyLabel': 'OK',
                          'cancelLabel': 'Anuluj'
                        }"
                        :class="{
                          'is-invalid': errors.length || (isValidated && !dateRange.startDate),
                          'w-100': true
                        }"
                        :singleDatePicker="true"
                        :timePicker="false"
                        :showWeekNumbers="false"
                        :showDropdowns="false"
                        v-model="dateRange"
                        :ranges="false"
                        :linkedCalendars="true"
                        :date-format="dateFormat">
                        <template v-slot:input="picker">
                          {{ prepareDateString(picker.startDate) }}
                        </template>
                      </date-range-picker>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </validation-observer>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się.
              <a
                href="javascript:window.location.reload();"
                class="alert-link">
                Odśwież stronę
              </a>
              aby spróbować ponownie.
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from '../../utils/FormUtils.js';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'goal-form',
  components: {
    'date-range-picker': DateRangePicker
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedUsers;
    },
    isEdit () {
      return !!this.$route.params.id;
    }
  },
  watch: {
    'dateRange.startDate': function (newValue) {
      if (newValue === null) {
        this.data.valid_from = null;
      }

      this.data.valid_from = this.prepareDateString(newValue);
    }
  },
  data() {
    return {
      data: {
        id: 0,
        valid_from: null,
        amount: 0,
        user_id: 0
      },
      loaded: false,
      loadError: false,
      dateRangePickerVisible: false,
      dateRange: {
        startDate: null
      },
      isValidated: false,
      allUsers: [],
      loadedUsers: false
    };
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
      this.dateRangePickerVisible = true;
    }
    this.loadAdditionalData();
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/kpi-schedules/item/',
        instanceFields: ['id', 'valid_from', 'amount', 'user_id'],
        loadedKey: 'loaded',
        successAction: (response) => {
          Vue.nextTick(() => {
            Vue.set(this.dateRange, 'startDate', new Date(response.valid_from));
            this.dateRangePickerVisible = true;
          });
        }
      });
    },
    validateForm () {
      this.isValidated = true;
      FormUtils.validate(this.$refs['goal-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/api/kpi-schedules/update/' + this.id : '/api/kpi-schedules/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano cel',
        successText: 'Wybrany cel został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja celu nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          this.$router.push('/cele')
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        valid_from: this.data.valid_from,
        amount: this.data.amount,
        user_id: this.data.user_id
      };

      return formDataToSend;
    },
    prepareDateString (input) {
      if (input === null) {
        return 'Wybierz datę';
      }
      input = new Date(input);

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    dateFormat (classes, date) {
      if (!classes.disabled) {
        classes.disabled = date.getDate() !== 1;
      }
      return classes
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/users/items',
        method: 'get',
        outputKey: 'allUsers',
        loadedKey: 'loadedUsers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.vue-daterange-picker.is-invalid {
  border: 1px solid #c4183c!important;
  border-radius: 0.25rem;
}
</style>
